import React, { useState, useEffect } from 'react';
import { Wand2, Scissors } from 'lucide-react';
import MediaPicker from './MediaPicker';
import { BRANDS } from '../lib/brands';
import { getCurrentUser } from '../services/auth';

/**
 * Starting a film: a title, a script, and the assets it is cut from.
 *
 * ⚠️ THE SCRIPT IS THE NARRATION, WORD FOR WORD - not a brief about it. The
 * planner cuts to the voice and the captions ARE the script, so a paraphrase
 * would put the wrong words on screen. If narration is generated here, this
 * exact text is what gets read.
 *
 * ⚠️ CLIP ORDER IS THE EDIT. Shots are used in the order chosen and the script
 * is divided across them in that order. Nothing decides which picture belongs
 * to which sentence - that is the one judgement a person still makes, and
 * pretending otherwise would produce confident nonsense.
 *
 * ⚠️ IT DOES NOT WAIT FOR THE PLAN. Planning downloads ffmpeg and measures the
 * narration, so it runs as a background function. Create returns as soon as the
 * project exists, and the studio screen shows the plan arriving.
 */

const FN = '/.netlify/functions/video-studio';
const AUDIO_FN = '/.netlify/functions/video-audio-generate';

async function post(url, action, params = {}) {
  const user = getCurrentUser();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(user && user.accessToken ? { Authorization: `Bearer ${user.accessToken}` } : {}),
    },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `${action} failed (${res.status})`);
  return data;
}

function Row({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
      <div style={{ flex: '0 0 128px', paddingTop: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{label}</div>
        {hint ? <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{hint}</div> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

export default function NewVideoProject({ onClose, onCreated, projectId = null, shotCount = 0 }) {
  // With a projectId the title, brand and script already exist and were
  // approved; this is only here to collect the assets.
  const attaching = !!projectId;
  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState('crab-place');
  const [script, setScript] = useState('');
  const [clips, setClips] = useState([]);
  const [narration, setNarration] = useState(null);
  const [music, setMusic] = useState(null);
  const [logo, setLogo] = useState(null);
  const [card, setCard] = useState({ tagline: '', headline: '', cta: '' });
  const [picking, setPicking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [el, setEl] = useState(null);
  const [voice, setVoice] = useState('');

  useEffect(() => {
    post(AUDIO_FN, 'status').then(setEl).catch(() => setEl({ configured: false }));
  }, []);

  const sentences = script.split(/(?<=[.!?])\s+/).map((t) => t.trim()).filter(Boolean);
  const ready = attaching
    ? (clips.length && narration)
    : (title.trim() && script.trim() && clips.length && narration);

  const generateNarration = async () => {
    setBusy(true); setErr(null);
    try {
      const d = await post(AUDIO_FN, 'narration', {
        text: script, voiceName: voice || undefined, brand,
        name: `${title || 'narration'}-narration`,
      });
      setNarration({ id: d.assetId, suggested_name: `${title || 'untitled'} narration (generated)` });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      if (attaching) {
        await post(FN, 'set-sources', {
          projectId,
          sources: {
            clip_asset_ids: clips.map((c) => c.id),
            narration_asset_id: narration ? narration.id : null,
            music_asset_id: music ? music.id : null,
            logo_asset_id: logo ? logo.id : null,
            end_card: card,
          },
        });
        onCreated(projectId);
        return;
      }
      const d = await post(FN, 'create', {
        title, brand, script,
        sources: {
          clip_asset_ids: clips.map((c) => c.id),
          narration_asset_id: narration ? narration.id : null,
          music_asset_id: music ? music.id : null,
          logo_asset_id: logo ? logo.id : null,
          end_card: card,
        },
      });
      onCreated(d.projectId);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 720, width: '100%' }}>
        <div className="card-header">{attaching ? `Choose the footage — ${shotCount} shots` : 'New film'}</div>
        <div className="card-body">
          {err ? (
            <div style={{ background: '#fdecec', color: '#c0392b', padding: '8px 12px', borderRadius: 5, marginBottom: 12, fontSize: 12 }}>{err}</div>
          ) : null}

          {attaching ? null : <Row label="Title">
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="The Parson of the Islands" />
          </Row>}

          {attaching ? null : <Row label="Brand" hint="decides the end card's type and colour">
            <select className="form-select" value={brand} onChange={(e) => setBrand(e.target.value)}>
              {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Row>}

          {attaching ? null : <Row label="Narration script" hint="the exact words, not a brief">
            <textarea className="form-textarea" rows={6} value={script} onChange={(e) => setScript(e.target.value)}
              placeholder="Write it as it should be heard. The captions are these words." />
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
              {sentences.length} sentence{sentences.length === 1 ? '' : 's'}
              {clips.length ? ` · grouped across ${clips.length} shot${clips.length === 1 ? '' : 's'}` : ''}
            </div>
          </Row>}

          <Row label="Clips" hint="used in the order you pick them">
            <button className="btn btn-outline" onClick={() => setPicking('clips')}>
              {clips.length ? `${clips.length} chosen — change` : 'Choose clips'}
            </button>
            {clips.length ? (
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5, wordBreak: 'break-word' }}>
                {clips.map((c) => c.suggested_name).join('  →  ')}
              </div>
            ) : null}
          </Row>

          <Row label="Narration audio">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-outline" onClick={() => setPicking('narration')}>
                {narration ? narration.suggested_name : 'Choose from the library'}
              </button>
              {el && el.configured ? (
                <>
                  <input className="form-input" style={{ width: 140 }} placeholder="voice name or ID"
                    value={voice} onChange={(e) => setVoice(e.target.value)} />
                  <button className="btn btn-ghost" disabled={busy || !script.trim()} onClick={generateNarration}>
                    <Wand2 size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Generate
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                  {el ? 'ElevenLabs not configured — add ELEVENLABS_API_KEY to Neon app_secrets to generate narration here'
                    : 'checking ElevenLabs…'}
                </span>
              )}
            </div>
          </Row>

          <Row label="Music bed" hint="optional, ducked under the voice">
            <button className="btn btn-outline" onClick={() => setPicking('music')}>
              {music ? music.suggested_name : 'Choose'}
            </button>
          </Row>

          <Row label="End card" hint="4 seconds after the last word">
            <input className="form-input" placeholder="Tagline" value={card.tagline}
              onChange={(e) => setCard({ ...card, tagline: e.target.value })} style={{ marginBottom: 6 }} />
            <input className="form-input" placeholder="HEADLINE" value={card.headline}
              onChange={(e) => setCard({ ...card, headline: e.target.value })} style={{ marginBottom: 6 }} />
            <input className="form-input" placeholder="Call to action" value={card.cta}
              onChange={(e) => setCard({ ...card, cta: e.target.value })} style={{ marginBottom: 8 }} />
            <button className="btn btn-outline" onClick={() => setPicking('logo')}>
              {logo ? `Logo: ${logo.suggested_name}` : 'Choose the logo'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5 }}>
              A card gets the real mark, never a typed wordmark.
            </div>
          </Row>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy || !ready} onClick={create}>
              <Scissors size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
              {busy ? 'Working…' : attaching ? 'Assemble it' : 'Plan the edit'}
            </button>
          </div>
        </div>
      </div>

      {picking ? (
        <MediaPicker
          multiSelect={picking === 'clips'}
          videosOnly={picking === 'clips'}
          audioOnly={picking === 'narration' || picking === 'music'}
          imagesOnly={picking === 'logo'}
          onClose={() => setPicking(null)}
          onPick={(sel) => {
            const arr = Array.isArray(sel) ? sel : [sel];
            if (picking === 'clips') setClips(arr);
            else if (picking === 'narration') setNarration(arr[0]);
            else if (picking === 'music') setMusic(arr[0]);
            else if (picking === 'logo') setLogo(arr[0]);
            setPicking(null);
          }}
        />
      ) : null}
    </div>
  );
}
